import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';
import { useSearchParams } from 'react-router-dom';
import { useInstanceId } from '../contexts/InstanceContext';
import { useTranslation } from '../hooks/useTranslation.jsx';
import API_ENDPOINTS from '../config/api';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';
import { useScreenStateUpdates } from '../../../../packages/shared/useScreenStateUpdates';
import {
  TYPE_ORDER, typeColor, computeClusterCenters, clusterHullPath,
  clusterMstEdges, pickLabeledNodes, labelTierForScale, labelFontScale, truncateTitle, escapeHtml, constrainPan,
  BASE_CLUSTER_KEY, BASE_CLUSTER_COLOR, clusterKeyOf, boundingCircle,
  radialLabelAnchor, toScreenPoint, defaultLabelYOffset, forceAvoidCenter,
} from '../utils/ragSourceMapLayout';

// specs/rag-source-heatmap.md — one bubble per RAG source, sized by how often
// it was retrieved, clustered by source type (publisher_type), with the
// always-on "Perusaineisto" base dataset pulled into its own central sphere.
// The physics simulation and its SVG scaffold are built once (setup effect
// below) and then only fed new data on each poll, so bubbles settle into
// place instead of resetting every refresh. Pan/zoom lives on its own <g>
// inside the SVG so touch/wheel gestures never touch the simulation.

const REFRESH_INTERVAL_MS = 20000;
const MONO_FONT = "'JetBrains Mono', 'SF Mono', monospace";
const ZOOM_MIN = 1;
const ZOOM_MAX = 6;
const ZOOM_PAN_RESISTANCE = 0.3;

function typeLabel(t, voice) {
  return t(`ragSourceMap.voices.${voice}`) || t('ragSourceMap.voices.tutkimus');
}

function contextLabel(t, context) {
  const key = `ragSourceMap.contexts.${context}`;
  const translated = t(key);
  if (translated == null || translated === key) {
    return context.charAt(0).toUpperCase() + context.slice(1).replace(/_/g, ' ');
  }
  return translated;
}

// Title-only label markup — nothing else (no type, no retrieval count).
// Sizes scale with `fontScale` (labelFontScale(k) — capped/damped, not 1:1
// with zoom, so titles stay a restrained, tooltip-like size instead of
// ballooning at high zoom) rather than relying on the ancestor SVG transform
// to visually scale foreignObject content, which isn't reliable across
// browsers — this layer lives in screen space, sized explicitly here and
// positioned by toScreenPoint/positionOverlays in the component. Titles are
// escaped (document titles come from indexed source metadata, not typed
// input, but this still crosses into raw HTML via d3's `.html()`).
function labelHtml(node, fontScale) {
  const title = escapeHtml(truncateTitle(node.title));
  const pad = `${(2 * fontScale).toFixed(1)}px ${(6 * fontScale).toFixed(1)}px`;
  const radius = `${(3 * fontScale).toFixed(1)}px`;
  return `<div xmlns="http://www.w3.org/1999/xhtml" style="
      font-family:${MONO_FONT}; font-size:${(10.5 * fontScale).toFixed(1)}px; letter-spacing:-0.02em;
      color:#fff; background:rgba(20,20,28,0.6); backdrop-filter:blur(6px);
      -webkit-backdrop-filter:blur(6px); border-radius:${radius}; padding:${pad};
      white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
      text-align:center;">${title}</div>`;
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  background: radial-gradient(ellipse at 50% 40%, #14141f 0%, #0a0a12 60%, #050508 100%);
  position: relative;
  overflow: hidden;

  html[data-platform='tizen'] & {
    background: #0a0a12;
  }
`;

const Title = styled.h1`
  position: absolute;
  top: 20px;
  left: 24px;
  color: rgba(255, 255, 255, 0.92);
  font-family: ${MONO_FONT};
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: -0.02em;
  margin: 0;
  z-index: 10;
`;

const SourceCounter = styled.div`
  position: absolute;
  top: 48px;
  left: 24px;
  color: rgba(255, 255, 255, 0.55);
  font-family: ${MONO_FONT};
  font-size: 0.78rem;
  letter-spacing: -0.01em;
  z-index: 10;
`;

const SVGContainer = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
  user-select: none;
`;

const StatusMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.55);
  font-family: ${MONO_FONT};
  font-size: 1rem;
  text-align: center;
`;

const Tooltip = styled.div`
  position: absolute;
  transform: translate(-50%, 0);
  pointer-events: none;
  z-index: 15;
  background: rgba(24, 24, 32, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 6px 10px;
  color: #fff;
  font-family: ${MONO_FONT};
  font-size: 12px;
  letter-spacing: -0.01em;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailPanel = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 320px;
  max-width: calc(100% - 48px);
  max-height: calc(100% - 48px);
  overflow-y: auto;
  background: rgba(14, 14, 22, 0.85);
  border: 1px solid ${props => props.accent}55;
  border-radius: 10px;
  padding: 20px;
  color: white;
  font-family: ${MONO_FONT};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  z-index: 20;
  box-shadow: 0 0 30px ${props => props.accent}22;
`;

const DetailTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  margin-bottom: 8px;
  padding-right: 20px;
  line-height: 1.35;
`;

const DetailType = styled.div`
  font-size: 0.75rem;
  color: ${props => props.accent};
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const BaseBadge = styled.div`
  display: inline-block;
  font-size: 0.68rem;
  color: ${BASE_CLUSTER_COLOR};
  border: 1px solid ${BASE_CLUSTER_COLOR}66;
  border-radius: 20px;
  padding: 1px 8px;
  margin: -6px 0 16px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const NotRetrievedBadge = styled.div`
  display: inline-block;
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.5);
  border: 1px dashed rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 1px 8px;
  margin: -6px 0 16px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailMeta = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 10px;
`;

const DetailDescription = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 16px;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const DetailDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 2px 0 16px;
`;

const DetailTotal = styled.div`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 2px;
`;

const DetailTotalLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 16px;
`;

const ContextRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  padding: 6px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: white;
  }
`;

const ResetButton = styled.button`
  position: absolute;
  bottom: 24px;
  right: 24px;
  z-index: 10;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(20, 20, 28, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: rgba(255, 255, 255, 0.75);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

function RagSourceMapScreen({ screenMode = false }) {
  useScreenNavigation({ screenMode });
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const instanceId = useInstanceId(searchParams);

  const [nodes, setNodes] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [zoomTransform, setZoomTransform] = useState({ k: 1, x: 0, y: 0 });

  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const simulationRef = useRef(null);
  const zoomBehaviorRef = useRef(null);
  const layersRef = useRef(null);
  const simNodesMapRef = useRef(new Map());
  const clusterCentersRef = useRef({});
  const clusterAnchorsRef = useRef({});
  const keepOutRadiusRef = useRef(60);
  const transformRef = useRef({ k: 1, x: 0, y: 0 });
  const labeledIdsRef = useRef(new Map());
  const dimensionsRef = useRef(dimensions);
  const detailPanelRef = useRef(null);

  useScreenStateUpdates('ragSourceMap', { sourceCount: nodes.length });

  useEffect(() => { dimensionsRef.current = dimensions; }, [dimensions]);

  // Close the detail panel on any click/tap outside it — but not on a bubble
  // itself, since selecting a different source already has its own d3 click
  // handler (mousedown fires before that click, so this only ever cancels a
  // selection when the click genuinely lands elsewhere). Must be a CAPTURE-
  // phase listener: d3-zoom's own mousedown handler on the <svg> (almost the
  // entire screen) calls stopImmediatePropagation() to claim the gesture,
  // which silently swallows a bubble-phase document listener before it ever
  // runs. Capture fires top-down, before that, so this always sees the click.
  useEffect(() => {
    if (!selectedNode) return undefined;
    const handleOutside = (event) => {
      if (detailPanelRef.current && detailPanelRef.current.contains(event.target)) return;
      if (event.target.closest && event.target.closest('.bubble')) return;
      setSelectedNode(null);
    };
    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
    };
  }, [selectedNode]);

  // Track the canvas size so the layout can fill it
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setDimensions({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Fetch, then poll, the aggregated source map for this instance
  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.AI_LOG_RAG_SOURCE_MAP(instanceId));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (cancelled) return;
        const fetchedNodes = data.nodes || [];
        setNodes(fetchedNodes);
        setStatus(fetchedNodes.length > 0 ? 'ready' : 'empty');
      } catch (err) {
        if (!cancelled) {
          console.error('RagSourceMapScreen: failed to load source map', err);
          setStatus('error');
        }
      }
    };

    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [instanceId]);

  // One cluster center per cluster key (publisher type, or 'base' for the
  // shared Perusaineisto corpus), ordered so related types land as ring
  // neighbors around the central base sphere.
  const clusterKeysKey = useMemo(
    () => [...new Set(nodes.map(n => clusterKeyOf(n)))].sort().join(','),
    [nodes],
  );
  const clusterCenters = useMemo(
    () => computeClusterCenters(clusterKeysKey ? clusterKeysKey.split(',') : [], dimensions.width, dimensions.height),
    [clusterKeysKey, dimensions.width, dimensions.height],
  );

  useEffect(() => {
    clusterCentersRef.current = clusterCenters;
    if (simulationRef.current) simulationRef.current.alpha(0.35).restart();
  }, [clusterCenters]);

  // Build the SVG scaffold (defs, zoom/pan root, layers), the physics
  // simulation, and the zoom behavior once. Everything after this only feeds
  // it data — the simulation object, its node positions, and the current
  // pan/zoom persist across polls and resizes.
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const defs = svg.append('defs');
    const glow = defs.append('filter').attr('id', 'rag-glow').attr('x', '-60%').attr('y', '-60%').attr('width', '220%').attr('height', '220%');
    glow.append('feGaussianBlur').attr('stdDeviation', 3).attr('result', 'blur');
    const feMerge = glow.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'blur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    const grid = defs.append('pattern')
      .attr('id', 'rag-grid').attr('width', 34).attr('height', 34).attr('patternUnits', 'userSpaceOnUse');
    grid.append('circle').attr('cx', 1).attr('cy', 1).attr('r', 1).attr('fill', 'rgba(255,255,255,0.05)');

    svg.append('rect').attr('width', '100%').attr('height', '100%').attr('fill', 'url(#rag-grid)');

    const zoomRoot = svg.append('g').attr('class', 'rag-zoom-root');
    const layers = {
      hullLayer: zoomRoot.append('g').attr('class', 'rag-hulls'),
      edgeLayer: zoomRoot.append('g').attr('class', 'rag-edges'),
      bubbleLayer: zoomRoot.append('g').attr('class', 'rag-bubbles'),
      // Source-title and cluster-title labels live OUTSIDE the zoomed <g>,
      // in screen space: their x/y/font-size are computed explicitly from
      // the current transform (positionOverlays, below) rather than relying
      // on the ancestor SVG transform to visually scale foreignObject
      // content, which browsers don't all do consistently. Cluster labels
      // are appended last (topmost) so bubbles never paint over them.
      labelLayer: svg.append('g').attr('class', 'rag-labels'),
      clusterLabelLayer: svg.append('g').attr('class', 'rag-cluster-labels'),
    };
    layersRef.current = layers;

    // The base cluster's "formal sphere" — a clean double ring, not an
    // organic hull, so it reads as a deliberate boundary around the corpus
    // every workshop shares (specs/rag-source-heatmap.md).
    layers.hullLayer.append('circle').attr('class', 'hull-base-outer')
      .attr('opacity', 0).attr('fill', BASE_CLUSTER_COLOR).attr('fill-opacity', 0.05)
      .attr('stroke', BASE_CLUSTER_COLOR).attr('stroke-opacity', 0.8).attr('stroke-width', 3);
    layers.hullLayer.append('circle').attr('class', 'hull-base-inner')
      .attr('opacity', 0).attr('fill', 'none')
      .attr('stroke', BASE_CLUSTER_COLOR).attr('stroke-opacity', 0.35).attr('stroke-width', 1);
    layers.clusterLabelLayer.append('text').attr('class', 'hull-label-base')
      .attr('opacity', 0).attr('text-anchor', 'middle').attr('fill', BASE_CLUSTER_COLOR)
      .style('font-family', MONO_FONT).style('font-size', '12px')
      .style('font-weight', '600').style('letter-spacing', '0.08em').style('text-transform', 'uppercase');

    // Positions (and, for cluster titles, sizes) every screen-space overlay
    // element from clusterAnchorsRef / live bubble data + the current
    // transform. Called from both tick() (while the simulation is settling)
    // and the zoom handler (while panning/zooming, when the simulation is
    // usually at rest and tick() isn't firing) — this is the one place that
    // touches the labelLayer/clusterLabelLayer DOM, so the two triggers
    // never fight each other.
    const positionOverlays = () => {
      const transform = transformRef.current;
      const fontScale = labelFontScale(transform.k);

      layers.labelLayer.selectAll('foreignObject.bubble-label').each(function labelPos(d) {
        const p = toScreenPoint(d.x, d.y + d.labelYOffset, transform);
        d3.select(this).attr('x', p.x - (d.labelWidth * fontScale) / 2).attr('y', p.y);
      });

      TYPE_ORDER.forEach(type => {
        const info = clusterAnchorsRef.current[type];
        const sel = layers.clusterLabelLayer.select(`text.hull-label-${type}`);
        if (info?.visible) {
          const p = toScreenPoint(info.anchor.x, info.anchor.y, transform);
          sel.attr('x', p.x).attr('y', p.y).attr('opacity', 1).style('font-size', `${(11 * fontScale).toFixed(1)}px`);
        } else {
          sel.attr('opacity', 0);
        }
      });

      const baseInfo = clusterAnchorsRef.current[BASE_CLUSTER_KEY];
      const baseSel = layers.clusterLabelLayer.select('text.hull-label-base');
      if (baseInfo?.visible) {
        const p = toScreenPoint(baseInfo.anchor.x, baseInfo.anchor.y, transform);
        baseSel.attr('x', p.x).attr('y', p.y).attr('opacity', 1).style('font-size', `${(12 * fontScale).toFixed(1)}px`);
      } else {
        baseSel.attr('opacity', 0);
      }
    };

    const tick = () => {
      const simNodes = simulationRef.current ? simulationRef.current.nodes() : [];
      const canvasCenter = { x: dimensionsRef.current.width / 2, y: dimensionsRef.current.height / 2 };

      layers.bubbleLayer.selectAll('circle.bubble')
        .attr('cx', d => d.x)
        .attr('cy', d => d.y);

      layers.edgeLayer.selectAll('line.rag-edge')
        .attr('x1', d => d[0].x).attr('y1', d => d[0].y)
        .attr('x2', d => d[1].x).attr('y2', d => d[1].y);

      // Cluster boundaries stay in local (zoomed) coordinates — only their
      // label anchor point needs converting to screen space, done below in
      // positionOverlays. The anchor sits just outside the cluster's own
      // bounding circle, in the direction away from the whole map's center,
      // so it lands on open canvas instead of behind another cluster's orbs.
      TYPE_ORDER.forEach(type => {
        const points = simNodes.filter(n => clusterKeyOf(n) === type).map(n => [n.x, n.y]);
        const path = clusterHullPath(points, 20);
        layers.hullLayer.select(`path.hull-${type}`).attr('d', path || null).attr('opacity', path ? 1 : 0);
        if (points.length > 0) {
          const circle = boundingCircle(points, 0);
          clusterAnchorsRef.current[type] = { anchor: radialLabelAnchor(circle, canvasCenter, 18), visible: true };
        } else {
          clusterAnchorsRef.current[type] = { visible: false };
        }
      });

      const basePoints = simNodes.filter(n => clusterKeyOf(n) === BASE_CLUSTER_KEY).map(n => [n.x, n.y]);
      const baseCircle = boundingCircle(basePoints, 34);
      if (baseCircle) {
        layers.hullLayer.select('circle.hull-base-outer')
          .attr('cx', baseCircle.cx).attr('cy', baseCircle.cy).attr('r', baseCircle.r).attr('opacity', 1);
        layers.hullLayer.select('circle.hull-base-inner')
          .attr('cx', baseCircle.cx).attr('cy', baseCircle.cy).attr('r', Math.max(baseCircle.r - 10, 0)).attr('opacity', 1);
        // The base sphere's own target center IS the canvas center, so its
        // live centroid only ever drifts a few px off it as bubbles jostle —
        // using the real canvas center here would make the "outward"
        // direction flip unstably tick to tick. Passing its own center as
        // the reference forces the deterministic straight-up fallback instead.
        clusterAnchorsRef.current[BASE_CLUSTER_KEY] = {
          anchor: radialLabelAnchor(baseCircle, { x: baseCircle.cx, y: baseCircle.cy }, 14),
          visible: true,
        };
        // Keep every non-base bubble physically out of the sphere as it
        // grows/shrinks with usage — a hard guarantee (not just a color/
        // position convention) that a source never visually reads as part of
        // both the base cluster and a publisher-type cluster at once.
        keepOutRadiusRef.current = baseCircle.r + 26;
      } else {
        layers.hullLayer.selectAll('circle.hull-base-outer, circle.hull-base-inner').attr('opacity', 0);
        clusterAnchorsRef.current[BASE_CLUSTER_KEY] = { visible: false };
        keepOutRadiusRef.current = 0;
      }

      positionOverlays();
    };

    const simulation = d3.forceSimulation([])
      .force('x', d3.forceX(d => clusterCentersRef.current[clusterKeyOf(d)]?.x ?? dimensionsRef.current.width / 2).strength(0.25))
      .force('y', d3.forceY(d => clusterCentersRef.current[clusterKeyOf(d)]?.y ?? dimensionsRef.current.height / 2).strength(0.25))
      .force('avoidBase', forceAvoidCenter({
        center: () => ({ x: dimensionsRef.current.width / 2, y: dimensionsRef.current.height / 2 }),
        keepOutRadius: () => keepOutRadiusRef.current,
        isExempt: d => clusterKeyOf(d) === BASE_CLUSTER_KEY,
      }))
      .force('collide', d3.forceCollide(d => d.r + 3))
      .force('charge', d3.forceManyBody().strength(-1.5))
      .alphaDecay(0.02)
      .on('tick', tick);

    simulationRef.current = simulation;

    // Pan/zoom — bounded to the map's own content area, with elastic
    // resistance while dragging past the edge (touch-friendly "rubber band"
    // instead of a hard wall) and a snap-back once the gesture ends.
    let lastEmit = 0;
    const zoomed = (event) => {
      const dims = dimensionsRef.current;
      const isGesture = !!event.sourceEvent;
      const constrained = constrainPan(event.transform, dims.width, dims.height, isGesture ? ZOOM_PAN_RESISTANCE : 0);
      zoomRoot.attr('transform', constrained.toString());
      transformRef.current = constrained;
      // Labels/cluster-titles live outside zoomRoot (screen space), so they
      // don't move for free with the SVG transform above — reposition them
      // on every raw zoom event, not just throttled ones, so they never lag
      // behind the bubbles they're pinned to.
      positionOverlays();
      if (isGesture) setHoveredNode(null);
      const now = performance.now();
      if (!isGesture || now - lastEmit > 120) {
        lastEmit = now;
        setZoomTransform({ k: constrained.k, x: constrained.x, y: constrained.y });
      }
    };

    const zoomBehavior = d3.zoom()
      .scaleExtent([ZOOM_MIN, ZOOM_MAX])
      .clickDistance(8)
      .on('zoom', zoomed)
      .on('end', (event) => {
        const dims = dimensionsRef.current;
        const hard = constrainPan(event.transform, dims.width, dims.height, 0);
        if (hard.k !== event.transform.k || hard.x !== event.transform.x || hard.y !== event.transform.y) {
          d3.select(svgRef.current).transition().duration(320).ease(d3.easeCubicOut).call(zoomBehavior.transform, hard);
        }
        setZoomTransform({ k: hard.k, x: hard.x, y: hard.y });
      });

    svg.call(zoomBehavior);
    zoomBehaviorRef.current = zoomBehavior;

    return () => {
      simulation.stop();
      simulationRef.current = null;
      svg.on('.zoom', null);
      zoomBehaviorRef.current = null;
    };
  }, []);

  // Merge freshly-polled data into the persistent simulation. Existing
  // sources keep their live x/y/vx/vy (no reset-to-center jitter); only
  // genuinely new sources spawn (at their cluster center) and animate in.
  // Handles bubbles, cluster hulls and the constellation edges — not the
  // title labels, which redraw independently as the zoom level changes.
  useEffect(() => {
    const layers = layersRef.current;
    if (!layers) return;

    const simMap = simNodesMapRef.current;
    const maxRetrievals = d3.max(nodes, n => n.totalRetrievals) || 1;
    const radiusScale = d3.scaleSqrt().domain([0, maxRetrievals]).range([4, 80]);
    const fallbackCenter = { x: dimensions.width / 2 || 400, y: dimensions.height / 2 || 300 };

    const incomingIds = new Set();
    let structuralChange = false;
    nodes.forEach(raw => {
      incomingIds.add(raw.documentId);
      const r = radiusScale(raw.totalRetrievals);
      const existing = simMap.get(raw.documentId);
      if (existing) {
        Object.assign(existing, {
          title: raw.title, totalRetrievals: raw.totalRetrievals,
          byContext: raw.byContext, voice: raw.voice, retrieved: raw.retrieved,
          isBaseDataset: raw.isBaseDataset, r,
          description: raw.description, publisher: raw.publisher, publicationYear: raw.publicationYear,
        });
      } else {
        structuralChange = true;
        const center = clusterCenters[clusterKeyOf(raw)] || fallbackCenter;
        simMap.set(raw.documentId, {
          documentId: raw.documentId, title: raw.title, totalRetrievals: raw.totalRetrievals,
          byContext: raw.byContext, voice: raw.voice, retrieved: raw.retrieved,
          isBaseDataset: raw.isBaseDataset, r,
          description: raw.description, publisher: raw.publisher, publicationYear: raw.publicationYear,
          x: center.x + (Math.random() - 0.5) * 40,
          y: center.y + (Math.random() - 0.5) * 40,
          vx: 0, vy: 0,
        });
      }
    });
    for (const id of Array.from(simMap.keys())) {
      if (!incomingIds.has(id)) {
        simMap.delete(id);
        structuralChange = true;
      }
    }

    // A brand-new or removed source needs the physics to actually place/undo
    // it, so reheat properly. Otherwise this is just usage counts ticking up
    // on existing sources — barely nudge the simulation (just enough for
    // forceCollide to make room for a grown neighbor) so the whole map holds
    // still instead of visibly re-settling on every poll.
    const simArray = Array.from(simMap.values());
    if (simulationRef.current) {
      simulationRef.current.nodes(simArray).alpha(structuralChange ? 0.5 : 0.06).restart();
    }

    // Bubbles — colored by voice cluster regardless of which cluster
    // (including the central base sphere) they've been pulled into. Radius
    // transitions smoothly on every merge, so a source gaining usage visibly
    // *grows* in place rather than jumping or (previously) never updating.
    // A never-retrieved source ("ei haettu") renders as a quiet, unfilled
    // dashed outline — present in the corpus but untouched, never as noise —
    // while a retrieved one ("haettu") keeps the solid glowing fill.
    layers.bubbleLayer.selectAll('circle.bubble')
      .data(simArray, d => d.documentId)
      .join(
        enter => enter.append('circle')
          .attr('class', 'bubble')
          .attr('cx', d => d.x).attr('cy', d => d.y)
          .attr('r', 0)
          .style('opacity', 0)
          .style('cursor', 'pointer')
          .on('click', (event, d) => setSelectedNode(d))
          .on('mouseenter', (event, d) => setHoveredNode(d))
          .on('mouseleave', () => setHoveredNode(null))
          .call(enter => enter.transition().duration(500)
            .attr('r', d => d.r).style('opacity', d => (d.retrieved ? 0.88 : 0.6))),
        update => update.call(sel => sel.transition().duration(900).ease(d3.easeCubicOut)
          .attr('r', d => d.r).style('opacity', d => (d.retrieved ? 0.88 : 0.6))),
        exit => exit.transition().duration(300).attr('r', 0).style('opacity', 0).remove(),
      )
      .attr('filter', d => (d.retrieved ? 'url(#rag-glow)' : null))
      .attr('fill', d => (d.retrieved ? typeColor(d.voice) : 'none'))
      .attr('stroke', d => typeColor(d.voice))
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', d => (d.retrieved ? null : '3,3'))
      .attr('stroke-opacity', d => (d.retrieved ? 0.6 : 0.45));

    // Cluster hull outlines (one persistent element per type, excluding
    // base-dataset documents — those live in the central sphere)
    layers.hullLayer.selectAll('path.hull')
      .data(TYPE_ORDER, d => d)
      .join(enter => enter.append('path')
        .attr('class', d => `hull hull-${d}`)
        .attr('opacity', 0)
        .attr('fill', d => typeColor(d))
        .attr('fill-opacity', 0.04)
        .attr('stroke', d => typeColor(d))
        .attr('stroke-opacity', 0.4)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '5,5'));

    // Group name labels — rendered in clusterLabelLayer (topmost) so bubbles
    // never cover them.
    layers.clusterLabelLayer.selectAll('text.hull-label')
      .data(TYPE_ORDER, d => d)
      .join(enter => enter.append('text')
        .attr('class', d => `hull-label hull-label-${d}`)
        .attr('opacity', 0)
        .attr('text-anchor', 'middle')
        .attr('fill', d => typeColor(d))
        .style('font-family', MONO_FONT)
        .style('font-size', '11px')
        .style('letter-spacing', '0.06em')
        .style('text-transform', 'uppercase'))
      .text(d => typeLabel(t, d));

    layers.clusterLabelLayer.select('text.hull-label-base').text(t('ragSourceMap.baseCluster'));

    // Intra-cluster connective lines (MST) — a light constellation, not a
    // full mesh. Topology fixed at merge time; only endpoints move per tick.
    const edgePairs = [];
    [...TYPE_ORDER, BASE_CLUSTER_KEY].forEach(key => {
      const clusterNodes = simArray.filter(n => clusterKeyOf(n) === key);
      clusterMstEdges(clusterNodes).forEach(pair => edgePairs.push(pair));
    });

    layers.edgeLayer.selectAll('line.rag-edge')
      .data(edgePairs, d => `${d[0].documentId}-${d[1].documentId}`)
      .join(
        enter => enter.append('line')
          .attr('class', 'rag-edge')
          .attr('x1', d => d[0].x).attr('y1', d => d[0].y)
          .attr('x2', d => d[1].x).attr('y2', d => d[1].y),
        update => update,
        exit => exit.remove(),
      )
      .attr('stroke', d => (clusterKeyOf(d[0]) === BASE_CLUSTER_KEY ? BASE_CLUSTER_COLOR : typeColor(d[0].voice)))
      .attr('stroke-opacity', 0.15)
      .attr('stroke-width', 1);
  }, [nodes, clusterCenters, dimensions.width, dimensions.height, t]);

  // Title labels redraw on their own, keyed to discrete zoom tiers: fully
  // zoomed out shows none (only cluster titles), then progressively the
  // biggest 20% / 50% / everyone as you zoom in (labelTierForScale) — title
  // text only, nothing else. Each label defaults to sitting just below its
  // own bubble — the same slot the hover tooltip uses (defaultLabelYOffset)
  // — and pickLabeledNodes only nudges it further down, in place, when that
  // spot is already taken, never hiding it outright unless truly out of
  // room. Reads the live simulation state directly so a pure pan/zoom
  // gesture never touches the physics or bubble/hull rendering; the box and
  // its text scale with labelFontScale(k) — capped/damped, not 1:1 with
  // zoom, so titles read as a restrained UI element (like the tooltip)
  // instead of ballooning at high zoom — while positionOverlays (setup
  // effect) keeps x/y glued to the bubble on every tick/zoom event in between.
  useEffect(() => {
    const layers = layersRef.current;
    if (!layers) return;

    const simArray = Array.from(simNodesMapRef.current.values());
    const scale = zoomTransform.k;
    const fontScale = labelFontScale(scale);
    const { percentile } = labelTierForScale(scale);
    const positions = pickLabeledNodes(simArray, { scale, percentile });
    labeledIdsRef.current = positions;
    const labeledNodes = simArray
      .filter(n => positions.has(n.documentId))
      .map(n => ({
        ...n,
        labelWidth: Math.min(truncateTitle(n.title).length * 6.2, 150) + 12,
        labelYOffset: positions.get(n.documentId).yOffset,
      }));

    layers.labelLayer.selectAll('foreignObject.bubble-label')
      .data(labeledNodes, d => d.documentId)
      .join(
        enter => enter.append('foreignObject')
          .attr('class', 'bubble-label')
          .attr('x', d => toScreenPoint(d.x, d.y + d.labelYOffset, zoomTransform).x - (d.labelWidth * fontScale) / 2)
          .attr('y', d => toScreenPoint(d.x, d.y + d.labelYOffset, zoomTransform).y)
          .style('pointer-events', 'none')
          .style('opacity', 0)
          .call(enter => enter.transition().duration(300).style('opacity', 1)),
        update => update,
        exit => exit.remove(),
      )
      .attr('width', d => d.labelWidth * fontScale)
      .attr('height', 20 * fontScale)
      .html(d => labelHtml(d, fontScale));
  }, [nodes, zoomTransform.k]);

  const resetZoom = () => {
    if (!zoomBehaviorRef.current || !svgRef.current) return;
    d3.select(svgRef.current).transition().duration(400).ease(d3.easeCubicOut)
      .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
  };

  const isZoomedOrPanned = zoomTransform.k > 1.01 || Math.abs(zoomTransform.x) > 1 || Math.abs(zoomTransform.y) > 1;
  const retrievedCount = nodes.reduce((count, n) => count + (n.retrieved ? 1 : 0), 0);

  return (
    <Container ref={containerRef}>
      <Title>{t('ragSourceMap.title')}</Title>
      {status === 'ready' && (
        <SourceCounter>
          {t('ragSourceMap.counter', { total: nodes.length, retrieved: retrievedCount })}
        </SourceCounter>
      )}

      <SVGContainer ref={svgRef} />

      {status === 'loading' && <StatusMessage>{t('ragSourceMap.loading')}</StatusMessage>}
      {status === 'empty' && <StatusMessage>{t('ragSourceMap.empty')}</StatusMessage>}
      {status === 'error' && <StatusMessage>{t('ragSourceMap.error')}</StatusMessage>}

      {isZoomedOrPanned && (
        <ResetButton onClick={resetZoom} aria-label={t('ragSourceMap.resetView')} title={t('ragSourceMap.resetView')}>
          {'⟲'}
        </ResetButton>
      )}

      {hoveredNode && !selectedNode && !labeledIdsRef.current.has(hoveredNode.documentId) && (() => {
        // Same "title slot" a persistent label would use (defaultLabelYOffset),
        // converted to screen space — so hovering an unlabeled orb previews
        // its title exactly where a label would sit, never as a second box.
        // A bubble that already has a persistent label skips the tooltip
        // entirely (see the guard above) instead of duplicating it.
        const yOffset = defaultLabelYOffset(hoveredNode, zoomTransform.k);
        const p = toScreenPoint(hoveredNode.x, hoveredNode.y + yOffset, zoomTransform);
        return (
          <Tooltip style={{ left: p.x, top: p.y }}>
            {truncateTitle(hoveredNode.title, 40)}
          </Tooltip>
        );
      })()}

      {selectedNode && (() => {
        const meta = [selectedNode.publisher, selectedNode.publicationYear].filter(Boolean).join(' · ');
        return (
          <DetailPanel ref={detailPanelRef} accent={typeColor(selectedNode.voice)}>
            <CloseButton onClick={() => setSelectedNode(null)}>&times;</CloseButton>
            <DetailTitle>{selectedNode.title}</DetailTitle>
            <DetailType accent={typeColor(selectedNode.voice)}>{typeLabel(t, selectedNode.voice)}</DetailType>
            {selectedNode.isBaseDataset && <BaseBadge>{t('ragSourceMap.baseCluster')}</BaseBadge>}
            {!selectedNode.retrieved && <NotRetrievedBadge>{t('ragSourceMap.notRetrieved')}</NotRetrievedBadge>}

            {meta && <DetailMeta>{meta}</DetailMeta>}
            {selectedNode.description && <DetailDescription>{selectedNode.description}</DetailDescription>}
            <DetailDivider />

            <DetailTotal>{selectedNode.totalRetrievals}</DetailTotal>
            <DetailTotalLabel>{t('ragSourceMap.retrievals')}</DetailTotalLabel>
            <div>
              {Object.entries(selectedNode.byContext || {})
                .sort((a, b) => b[1] - a[1])
                .map(([context, count]) => (
                  <ContextRow key={context}>
                    <span>{contextLabel(t, context)}</span>
                    <span>{count}</span>
                  </ContextRow>
                ))}
            </div>
          </DetailPanel>
        );
      })()}
    </Container>
  );
}

export default RagSourceMapScreen;
